import { useEffect, useState } from "react";
import { api } from "@/lib/api";

/**
 * Detects if the current hostname is a school subdomain (e.g. wend-panga.kosyam.com)
 * and resolves it to a school record via /api/public/schools/by-slug/{slug}.
 *
 * Reserved (non-school) subdomains: "www", "app", "api", "admin", "preview".
 *
 * Returns: { loading, school, slug } where school is null if not a school subdomain.
 */
const RESERVED = new Set(["www", "app", "api", "admin", "preview", "staging", "dev", "test"]);

export function extractSubdomainSlug(hostname) {
  if (!hostname) return null;
  const host = hostname.toLowerCase();
  // Skip localhost / IPs
  if (host === "localhost" || /^\d+\.\d+\.\d+\.\d+$/.test(host)) return null;
  const parts = host.split(".");
  // Need at least 3 parts (sub.domain.tld) — or 4 if using .emergentagent.com preview host
  if (parts.length < 3) return null;
  const sub = parts[0];
  if (RESERVED.has(sub)) return null;
  // Ignore emergent preview host (single-tenant preview subdomain, not a school)
  if (host.endsWith(".preview.emergentagent.com")) return null;
  // Very short subdomains are unlikely to be school slugs
  if (sub.length < 2) return null;
  return sub;
}

export default function useSubdomainSchool() {
  const [state, setState] = useState({ loading: true, school: null, slug: null });
  useEffect(() => {
    const slug = extractSubdomainSlug(window.location.hostname);
    if (!slug) {
      setState({ loading: false, school: null, slug: null });
      return;
    }
    (async () => {
      try {
        const r = await api.get(`/public/schools/by-slug/${slug}`);
        setState({ loading: false, school: r.data, slug });
      } catch {
        // Unknown subdomain — treat as main site
        setState({ loading: false, school: null, slug });
      }
    })();
  }, []);
  return state;
}
